import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, MessageCircle, Phone, Mail, Clock, CheckCircle2, Calendar } from "lucide-react";
import { addDays, format } from "date-fns";
import { es } from "date-fns/locale";
import {
  PROJECT_TYPE_OPTIONS,
  SQUARE_METER_OPTIONS,
  CITY_OPTIONS,
  BRAND,
  buildWhatsAppUrl,
} from "../../lib/constants";
import { submitQuoteLead, trackEvent, gtmPush } from "../../lib/api";

const MX_PHONE_RE = /^(\+?52\s?)?[\s\-\(\)]*(\d[\s\-\(\)]*){10,}$/;

const initial = {
  full_name: "",
  phone: "",
  email: "",
  project_type: "",
  square_meters: "",
  city: "",
  description: "",
  has_plans: "",
};

export default function QuoteForm() {
  const navigate = useNavigate();
  const [data, setData] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const formRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const { typeKey, squareMeters, city, hasPlans } = e.detail || {};
      setData((prev) => ({
        ...prev,
        ...(typeKey ? { project_type: typeKey } : {}),
        ...(squareMeters ? { square_meters: squareMeters } : {}),
        ...(city ? { city } : {}),
        ...(hasPlans ? { has_plans: hasPlans } : {}),
      }));
    };
    window.addEventListener("vt:preselectProjectType", handler);
    window.addEventListener("vt:prefillQuote", handler);
    return () => {
      window.removeEventListener("vt:preselectProjectType", handler);
      window.removeEventListener("vt:prefillQuote", handler);
    };
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const earliestSlot = useMemo(() => {
    const target = addDays(new Date(), 7);
    return format(target, "EEEE d 'de' MMMM", { locale: es });
  }, []);

  const validate = () => {
    const e = {};
    if (!data.full_name.trim() || data.full_name.trim().length < 2) e.full_name = "Ingresa tu nombre completo.";
    if (!MX_PHONE_RE.test(data.phone.trim())) e.phone = "Teléfono inválido (formato MX).";
    if (data.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) e.email = "Correo inválido.";
    if (!data.project_type) e.project_type = "Selecciona un tipo.";
    if (!data.square_meters) e.square_meters = "Selecciona un rango.";
    if (!data.city) e.city = "Selecciona una ciudad.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        ...data,
        email: data.email || undefined,
        description: data.description || undefined,
        has_plans: data.has_plans || undefined,
      };
      const res = await submitQuoteLead(payload);
      trackEvent("lead_quote_submitted", { score: res.score, project_type: data.project_type });
      gtmPush("lead_submit", {
        lead_id: res.id,
        lead_score: res.score,
        lead_tier: res.tier || "unclassified",
        project_type: data.project_type,
        square_meters: data.square_meters,
        city: data.city,
      });

      // Find human label for project_type from options array
      const ptOpt = PROJECT_TYPE_OPTIONS.find((o) => o.value === data.project_type);
      const projectTypeLabel = ptOpt ? ptOpt.label : data.project_type;

      // Redirect to /gracias with lead state
      navigate("/gracias", {
        state: {
          lead_id: res.id,
          lead_score: res.score,
          lead_tier: res.tier,
          project_type: data.project_type,
          project_type_label: projectTypeLabel,
          full_name: data.full_name,
          whatsapp_url: res.whatsapp_url,
          estimated_value_mxn: res.estimated_value_mxn,
        },
      });
      setData(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail || "No pudimos enviar tu solicitud. Inténtalo de nuevo.";
      toast.error(typeof detail === "string" ? detail : "Error al enviar el formulario.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="cotiza" ref={formRef} data-testid="quote-form-section" className="bg-[#3D5311] text-white section-pad relative">
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-accent hidden md:block" />
      <div className="container-tight grid grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-10 lg:gap-16">
        {/* Left: form */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <span className="divider-accent" />
            <span className="kicker-bright">Cotización gratuita</span>
          </div>
          <h2 className="font-display font-black tracking-tightest text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
            Obtén tu diseño y<br />
            <span className="text-accent">cotización</span> sin costo.
          </h2>
          <p className="mt-6 max-w-xl text-white/70 text-base md:text-lg">
            Un ingeniero especialista te contacta en menos de 24 horas hábiles.
          </p>

          {!success && (
            <div
              className="mt-8 flex items-start gap-3 bg-eclipse-800/80 border-l-4 border-accent px-5 py-4"
              data-testid="quote-urgency-banner"
            >
              <Calendar className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <p className="text-[13px] text-white/85 leading-relaxed">
                <span className="text-white font-semibold">Agenda con 7 días de anticipación:</span>{" "}
                nuestros ingenieros tienen agenda disponible para visitas técnicas a partir del{" "}
                <span className="text-accent font-mono uppercase tracking-wider">{earliestSlot}</span>.{" "}
                Solicita ya tu fecha.
              </p>
            </div>
          )}

          {success ? (
            <div
              className="mt-10 border border-accent bg-eclipse-800 p-8 md:p-10"
              data-testid="quote-success-card"
            >
              <div className="flex items-start gap-4">
                <CheckCircle2 className="w-10 h-10 text-accent flex-shrink-0" />
                <div>
                  <h3 className="font-display font-black text-2xl md:text-3xl text-white tracking-tight">
                    ¡Listo! Recibimos tu solicitud.
                  </h3>
                  <p className="mt-3 text-white/75">
                    Un ingeniero te contactará en menos de 24 horas hábiles. Si quieres avanzar más rápido, escríbenos por WhatsApp ahora mismo.
                  </p>
                  <a
                    href={success.whatsapp_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#25D366] text-white font-display font-bold uppercase tracking-wider text-sm hover:brightness-110 transition"
                    data-testid="quote-success-whatsapp"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Ir a WhatsApp ahora
                  </a>
                  <button
                    onClick={() => setSuccess(null)}
                    className="ml-3 mt-6 font-mono text-xs uppercase tracking-widest text-white/60 hover:text-accent"
                    data-testid="quote-success-reset"
                  >
                    Enviar otra cotización
                  </button>
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <button
                      onClick={() => {
                        const el = document.getElementById("proyectos");
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="font-mono uppercase text-[11px] tracking-widest text-white/60 hover:text-accent inline-flex items-center gap-2"
                      data-testid="quote-success-gallery-link"
                    >
                      o explora otros proyectos mientras te contactamos →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" noValidate data-testid="quote-form">
              <Field label="Nombre completo *" error={errors.full_name} full>
                <input
                  type="text"
                  value={data.full_name}
                  onChange={(e) => set("full_name", e.target.value)}
                  placeholder="Tu nombre"
                  className="vt-input"
                  data-testid="quote-input-name"
                  required
                />
              </Field>

              <Field label="Teléfono *" error={errors.phone}>
                <input
                  type="tel"
                  value={data.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+52 (81) ____-____"
                  className="vt-input"
                  data-testid="quote-input-phone"
                  required
                />
              </Field>

              <Field label="Email (opcional)" error={errors.email}>
                <input
                  type="email"
                  value={data.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="correo@empresa.com"
                  className="vt-input"
                  data-testid="quote-input-email"
                />
              </Field>

              <Field label="Tipo de proyecto *" error={errors.project_type}>
                <select
                  value={data.project_type}
                  onChange={(e) => set("project_type", e.target.value)}
                  className="vt-input"
                  data-testid="quote-select-project-type"
                  required
                >
                  <option value="">Selecciona…</option>
                  {PROJECT_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Metros cuadrados aprox. *" error={errors.square_meters}>
                <select
                  value={data.square_meters}
                  onChange={(e) => set("square_meters", e.target.value)}
                  className="vt-input"
                  data-testid="quote-select-sqm"
                  required
                >
                  <option value="">Selecciona…</option>
                  {SQUARE_METER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </Field>

              <Field label="Ciudad *" error={errors.city}>
                <select
                  value={data.city}
                  onChange={(e) => set("city", e.target.value)}
                  className="vt-input"
                  data-testid="quote-select-city"
                  required
                >
                  <option value="">Selecciona…</option>
                  {CITY_OPTIONS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>

              <Field label="Descripción breve" optional full>
                <textarea
                  rows={3}
                  value={data.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Cuéntanos sobre tu espacio..."
                  className="vt-input resize-none"
                  data-testid="quote-input-description"
                />
              </Field>

              <Field label="¿Tienes planos o renders?" optional full>
                <div className="flex flex-wrap gap-3" data-testid="quote-has-plans">
                  {[
                    { v: "si", l: "Sí, tengo planos" },
                    { v: "proceso", l: "En proceso" },
                    { v: "no", l: "Aún no" },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      className={`cursor-pointer font-mono uppercase text-xs tracking-widest px-4 py-3 border transition-colors ${
                        data.has_plans === opt.v
                          ? "bg-accent border-accent text-white"
                          : "border-white/20 text-white/70 hover:border-accent hover:text-accent"
                      }`}
                    >
                      <input
                        type="radio"
                        name="has_plans"
                        value={opt.v}
                        className="hidden"
                        checked={data.has_plans === opt.v}
                        onChange={() => set("has_plans", opt.v)}
                      />
                      {opt.l}
                    </label>
                  ))}
                </div>
              </Field>

              <div className="md:col-span-2 mt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-accent w-full md:w-auto text-base py-5 px-10 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="quote-submit"
                >
                  {loading ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-4 h-4 border-2 border-eclipse border-t-transparent rounded-full animate-spin" />
                      Enviando…
                    </span>
                  ) : (
                    "Quiero mi cotización gratis"
                  )}
                </button>
                <p className="mt-4 flex items-center gap-2 text-white/50 text-xs font-mono">
                  <Lock className="w-3.5 h-3.5" /> Tus datos están seguros. Respondemos en menos de 24 horas hábiles.
                </p>
              </div>
            </form>
          )}
        </div>

        {/* Right: direct contact card */}
        <aside className="relative">
          <div className="sticky top-28 border border-white/10 bg-eclipse-800 p-8 md:p-10">
            <span className="kicker-bright">Prefiero hablar directo</span>
            <h3 className="mt-3 font-display font-black text-2xl md:text-3xl text-white tracking-tight leading-tight">
              Llama o escríbenos ahora mismo
            </h3>
            <p className="mt-4 text-white/70 text-sm">
              Si quieres respuesta inmediata, nuestro equipo te atiende por WhatsApp en horario laboral.
            </p>

            <a
              href={buildWhatsAppUrl("Hola, quiero información y cotización de una velaria.")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("cta_whatsapp_click", { location: "quote_aside" })}
              className="mt-6 inline-flex w-full items-center justify-center gap-3 px-6 py-5 bg-[#25D366] text-white font-display font-black uppercase tracking-wider hover:brightness-110 transition"
              data-testid="quote-aside-whatsapp"
            >
              <MessageCircle className="w-5 h-5" /> WhatsApp ahora
            </a>

            <div className="mt-8 space-y-4 text-white/80 text-sm">
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-accent" />
                <a href={`tel:${BRAND.phoneTel}`} className="hover:text-accent">{BRAND.phoneDisplay}</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-accent" />
                <a href={`mailto:${BRAND.emailSales}`} className="hover:text-accent break-all">{BRAND.emailSales}</a>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-accent mt-0.5" />
                <span className="font-mono text-xs leading-relaxed">{BRAND.hours}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {/* Shared input style */}
      <style>{`
        .vt-input {
          width: 100%;
          background: #1F2937;
          border: 1px solid rgba(255,255,255,0.15);
          color: #fff;
          padding: 14px 16px;
          font-family: 'Inter', sans-serif;
          font-size: 15px;
          outline: none;
          transition: border-color 200ms ease;
        }
        .vt-input::placeholder { color: rgba(255,255,255,0.4); }
        .vt-input:focus { border-color: #8cb62c; }
        .vt-input:hover { border-color: rgba(255,255,255,0.3); }
      `}</style>
    </section>
  );
}

function Field({ label, error, children, full, optional, success }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 flex items-center gap-2">
        {label}
        {optional && <span className="text-white/35 normal-case tracking-normal font-sans text-[10px] px-1.5 py-0.5 border border-white/15">opcional</span>}
        {success && <span className="text-green-400 font-bold" data-testid="phone-valid-check" aria-label="válido">✓</span>}
      </span>
      <div className="mt-2">{children}</div>
      {error && <span className="mt-1 block text-xs text-accent font-mono">{error}</span>}
    </label>
  );
}
